import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Notification,
  Logging,
  API,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  Setting,
  toBoolean,
  settingECWFormPrecedence,
  settingECWMultiJurisdictionFormPrecedence,
  settingECWFormUpload,
  settingECWProductQuestion,
  settingECWStatutoryExemptCustomers,
  settingECWCertificateScope,
  settingECWProductListingPage,
  settingECWAdditionalDocumentation,
  SettingsContextObject,
  SettingsContext,
  updateSettingsObject,
  renderCheckboxWithHeading,
  renderRadioGroup,
  RadioOption,
  Footer,
  getUuidV4,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import SettingsProps from './SettingsProps.interface';
import '../common-ecwizard.scss';
import './settings.scss';
import useECWizardSettings from './useECWizardSettings';

const controlStyle: ControlStyleClass = null;

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlValidation: ControlValidation = {
  requiredFields: ['formPrecedence', 'multiJurisdictionFormPrecedence'],
  customMessages: null,
};

const formPrecedenceOptions: RadioOption[] = [
  {
    data: 0,
    display: 'Prefer state-specific forms',
  },
  {
    data: 1,
    display: 'Prefer multi-jurisdiction forms',
  },
  {
    data: 2,
    display: 'Prefer multi-jurisdiction forms when applicable to 2+ selected jurisdictions',
  },
];

const multiJurisdictionFormPrecedenceOptions: RadioOption[] = [
  {
    data: 0,
    display: 'MTC',
  },
  {
    data: 1,
    display: 'Streamlined',
  },
];

const Settings = ({
  gatewayEndPoint,
}: SettingsProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [reloadKey, setReloadKey] = React.useState(getUuidV4());
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);

  // Load the EC Wizard Settings
  const [ecWizardSettings, ecWizardSettingsObject, ecWizardSettingsNotification] = useECWizardSettings(gatewayEndPoint, reloadKey);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);

  const [formPrecedence, setFormPrecedence] = React.useState(null);
  const [multiJurisdictionFormPrecedence, setMultiJurisdictionFormPrecedence] = React.useState(null);
  const [formUpload, setFormUpload] = React.useState(null);
  const [productQuestion, setProductQuestion] = React.useState(null);
  const [statutoryExemptCustomers, setStatutoryExemptCustomers] = React.useState(null);
  const [certificateScope, setCertificateScope] = React.useState(null);
  const [productListingPage, setProductListingPage] = React.useState(null);
  const [additionalDocumentation, setAdditionalDocumentation] = React.useState(null);

  // Get the Updated Settings
  const getUpdatedSettings = (): Setting[] => {
    const updatedSettings: Setting[] = [];

    updatedSettings.push({
      name: settingECWFormPrecedence,
      sequenceNumber: 1,
      activeInd: true,
      value: formPrecedence,
      dataType: 'SETTING_LIST',
    });

    updatedSettings.push({
      name: settingECWMultiJurisdictionFormPrecedence,
      sequenceNumber: 1,
      activeInd: true,
      value: multiJurisdictionFormPrecedence,
      dataType: 'SETTING_LIST',
    });

    updatedSettings.push({
      name: settingECWFormUpload,
      sequenceNumber: 1,
      activeInd: true,
      value: formUpload,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingECWProductQuestion,
      sequenceNumber: 1,
      activeInd: true,
      value: productQuestion,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingECWStatutoryExemptCustomers,
      sequenceNumber: 1,
      activeInd: true,
      value: statutoryExemptCustomers,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingECWCertificateScope,
      sequenceNumber: 1,
      activeInd: true,
      value: certificateScope,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingECWProductListingPage,
      sequenceNumber: 1,
      activeInd: true,
      value: productListingPage,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingECWAdditionalDocumentation,
      sequenceNumber: 1,
      activeInd: true,
      value: additionalDocumentation,
      dataType: 'BOOLEAN',
    });

    return updatedSettings;
  };

  // Save the Settings
  const saveSettings = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);
      const updatedSettings: Setting[] = getUpdatedSettings();

      // Save the Settings
      await api.settings.setCertificateSettingsV4(updatedSettings);

      // Update the Settings Object
      const settingsObject: SettingsContext = updateSettingsObject(settingsContext, 'ecWizard', updatedSettings);

      // Update the Certicate Settings Context
      setSettingsContext(settingsObject);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'EC Wizard Settings Saved',
        description: 'The EC Wizard Settings were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);

      // Reload the Settings
      setReloadKey(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'EC Wizard Settings Save Error',
        description: 'Unable to Save the EC Wizard Settings.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    dispatch(saveSettings());
  };

  // Get a Boolean Setting Value
  const getECWizardSettingBoolean = (name: string): boolean => {
    Logging.Debug('getECWizardSetting', name);
    const filteredSettings: Setting[] = ecWizardSettings?.filter((x: Setting) => x.name === name);

    if (filteredSettings && filteredSettings.length === 1) {
      return filteredSettings[0].value !== null ? toBoolean(filteredSettings[0].value) : null;
    }

    return null;
  };

  // Get a Number Setting Value
  const getECWizardSettingNumber = (name: string): number => {
    Logging.Debug('getECWizardSetting', name);
    const filteredSettings: Setting[] = ecWizardSettings?.filter((x: Setting) => x.name === name);

    if (filteredSettings && filteredSettings.length === 1) {
      return filteredSettings[0].value !== null ? Number(filteredSettings[0].value) : null;
    }

    return null;
  };

  // Cancel Button was Pressed
  const onCancel = (): void => {
    setFormPrecedence(getECWizardSettingNumber(settingECWFormPrecedence));
    setMultiJurisdictionFormPrecedence(getECWizardSettingNumber(settingECWMultiJurisdictionFormPrecedence));
    setFormUpload(getECWizardSettingBoolean(settingECWFormUpload));
    setProductQuestion(getECWizardSettingBoolean(settingECWProductQuestion));
    setStatutoryExemptCustomers(getECWizardSettingBoolean(settingECWStatutoryExemptCustomers));
    setCertificateScope(getECWizardSettingBoolean(settingECWCertificateScope));
    setProductListingPage(getECWizardSettingBoolean(settingECWProductListingPage));
    setAdditionalDocumentation(getECWizardSettingBoolean(settingECWAdditionalDocumentation));
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  // Loading is Done - Successfull
  React.useEffect(() => {
    if (ecWizardSettings) {
      setIsLoading(false);

      setFormPrecedence(getECWizardSettingNumber(settingECWFormPrecedence));
      setMultiJurisdictionFormPrecedence(getECWizardSettingNumber(settingECWMultiJurisdictionFormPrecedence));
      setFormUpload(getECWizardSettingBoolean(settingECWFormUpload));
      setProductQuestion(getECWizardSettingBoolean(settingECWProductQuestion));
      setStatutoryExemptCustomers(getECWizardSettingBoolean(settingECWStatutoryExemptCustomers));
      setCertificateScope(getECWizardSettingBoolean(settingECWCertificateScope));
      setProductListingPage(getECWizardSettingBoolean(settingECWProductListingPage));
      setAdditionalDocumentation(getECWizardSettingBoolean(settingECWAdditionalDocumentation));
    }
  }, [ecWizardSettings]);

  // Loading is Done - Error
  React.useEffect(() => {
    if (ecWizardSettingsNotification) {
      setIsLoading(false);
      setNotification(ecWizardSettingsNotification);
    }
  }, [ecWizardSettingsNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Settings: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setIsLoading(true);
      setNotification(null);
    };
  }, []);

  return (
    <>
      {
        isLoading
          ? (
            <ContentLoading loadingMessage="Loading Settings ..." />
          )
          : (
            <div className="ecwizardContainer">
              <div className="ecwizardHeader">
                <div className="ecwizardHeaderLeft">
                  Wizard Settings
                </div>
              </div>
              <hr />
              {
                ecWizardSettings && (
                  <>
                    <div className="ecwizardContentControls">
                      <div className="infoBanner">
                        <div className="infoBannerIcon">
                          <VtxIcon type="info-circle" color="white" fontSize={20} />
                        </div>
                        <div className="infoBannerDescription">
                          Below are the global settings that are used by default, unless you manually change the settings for a seller.
                        </div>
                      </div>
                      {renderRadioGroup(100, 'formPrecedence', 'Form Precedence', formPrecedenceOptions, 'horizontal',
                        formPrecedence, setFormPrecedence, controlStateEnabled, controlStyle, controlValidation)}
                      {renderRadioGroup(100, 'multiJurisdictionFormPrecedence', 'Multi-Jurisdiction Form Precedence',
                        multiJurisdictionFormPrecedenceOptions, 'horizontal', multiJurisdictionFormPrecedence,
                        setMultiJurisdictionFormPrecedence, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'formUpload', 'Form Upload', 'Allows forms to be uploaded',
                        formUpload, setFormUpload, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'certificateScope', 'Certificate Scope', 'Always create blanket certificates',
                        certificateScope, setCertificateScope, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'productQuestion', 'Product Question', 'Always ask product question',
                        productQuestion, setProductQuestion, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'productListingPage', 'Product Listing Page', 'Use separate page(s) to display exempt products on a certificate', // eslint-disable-line max-len
                        productListingPage, setProductListingPage, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'statutoryExemptCustomers', 'Statutory Exempt Customers', 'Allow user to create and submit a declaration for governments and public schools', // eslint-disable-line max-len
                        statutoryExemptCustomers, setStatutoryExemptCustomers, controlStateEnabled, controlStyle, controlValidation)}
                      {renderCheckboxWithHeading(50, 'additionalDocumentation', 'Additional Documentation', 'Allow the upload of additional documentation when creating or uploading a certificate.', // eslint-disable-line max-len
                        additionalDocumentation, setAdditionalDocumentation, controlStateEnabled, controlStyle, controlValidation)}
                    </div>
                    <Footer onCancel={onCancel} onSave={onSave} />
                  </>
                )
              }
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default Settings;
